import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, FormControlLabel, MenuItem, Paper, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../services/api";

/*
 * Configurações > Sistema do cliente (só administrador).
 * A chave de integração é de ESCRITA: o servidor nunca a devolve; a tela só
 * sabe se ela existe ("temChave"). Cada atendente é ligado ao seu usuário no
 * sistema — é daí que o servidor tira o X-Usuario.
 */

const ROTULOS = { sabegestao: "Sabe Gestão Web" };

const erro = (e) => {
  const c = e && e.response && e.response.data && e.response.data.error;
  const m = {
    ERR_INTEGRACAO_URL: "Endereço não permitido: use https://cliente.sabesistemas.com.br/api/v1",
    ERR_INTEGRACAO_BANCO: "Informe o banco do cliente.",
    ERR_INTEGRACAO_NAO_ENCONTRADO: "Esse usuário não existe no sistema.",
    ERR_INTEGRACAO_USUARIO_INVALIDO: "Esse usuário está desativado no sistema.",
    ERR_INTEGRACAO_CHAVE_INVALIDA: "A chave foi recusada pelo sistema.",
    ERR_INTEGRACAO_INDISPONIVEL: "O sistema não respondeu.",
    ERR_INTEGRACAO_DESLIGADA: "Salve a configuração do sistema primeiro."
  };
  toast.error(m[c] || "Não foi possível salvar.");
};

export default function SistemaCliente() {
  const [cfg, setCfg] = useState({ sistema: "sabegestao", nome: "", baseUrl: "https://cliente.sabesistemas.com.br/api/v1", banco: "", ativo: false, temChave: false });
  const [sistemas, setSistemas] = useState(["sabegestao"]);
  const [chave, setChave] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [edicao, setEdicao] = useState({});

  const carregar = async () => {
    try {
      const { data } = await api.get("/integracao/config");
      if (data.config) setCfg(data.config);
      setSistemas(data.sistemas || ["sabegestao"]);
      const u = await api.get("/integracao/usuarios");
      setUsuarios(u.data);
      setEdicao({});
    } catch (e) { erro(e); }
  };
  useEffect(() => { carregar(); }, []);

  const salvar = async () => {
    try {
      const corpo = { ...cfg };
      if (chave.trim()) corpo.chave = chave.trim();
      delete corpo.temChave;
      const { data } = await api.put("/integracao/config", corpo);
      if (data.config) setCfg(data.config);
      setChave("");
      toast.success("Configuração salva.");
    } catch (e) { erro(e); }
  };

  const salvarUsuario = async (u) => {
    try {
      const v = edicao[u.id];
      const { data } = await api.put(`/integracao/usuarios/${u.id}`, { idUsuarioExterno: v === "" ? null : v });
      toast.success(data.nomeExterno ? `${u.name} ligado a ${data.nomeExterno}${data.administrador ? " (administrador)" : ""}.` : `${u.name} desligado.`);
      carregar();
    } catch (e) { erro(e); }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 900 }}>
      <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6">Sistema do cliente</Typography>
        <Typography variant="body2" color="text.secondary">
          Liga os contatos do SabeZap ao cadastro do cliente no sistema da empresa. O atendente vê só o que vê no sistema:
          a janela aparece se a empresa usa o módulo e o usuário dele tem o acesso. Sem configuração, nada muda no SabeZap.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <TextField id="integ-sistema" select size="small" label="Sistema" value={cfg.sistema} onChange={(e) => setCfg({ ...cfg, sistema: e.target.value })}>
            {sistemas.map((s) => <MenuItem key={s} value={s}>{ROTULOS[s] || s}</MenuItem>)}
          </TextField>
          <TextField id="integ-nome" size="small" label="Nome (aparece para você)" value={cfg.nome || ""} onChange={(e) => setCfg({ ...cfg, nome: e.target.value })} />
          <TextField id="integ-url" size="small" label="Endereço da API" value={cfg.baseUrl || ""} onChange={(e) => setCfg({ ...cfg, baseUrl: e.target.value })} />
          <TextField id="integ-banco" size="small" label="Banco do cliente" value={cfg.banco || ""} onChange={(e) => setCfg({ ...cfg, banco: e.target.value })} />
          <TextField
            id="integ-chave" size="small" type="password" autoComplete="new-password"
            label={cfg.temChave ? "Chave de integração (guardada; preencha só para trocar)" : "Chave de integração"}
            value={chave} onChange={(e) => setChave(e.target.value)}
          />
          <FormControlLabel control={<Switch checked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })} />} label="Ligado" />
        </Box>
        <Box><Button variant="contained" onClick={salvar}>Salvar</Button>{cfg.temChave && <Chip size="small" label="chave guardada no servidor" sx={{ ml: 2 }} />}</Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, overflowX: "auto" }}>
        <Typography variant="h6">Atendentes</Typography>
        <Typography variant="body2" color="text.secondary">Informe o número do usuário (idusuario) de cada atendente no sistema. Quem não estiver ligado não vê a integração.</Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>Atendente</TableCell><TableCell>Usuário no sistema</TableCell><TableCell sx={{ width: 150 }}>idusuario</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {usuarios.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.name}<Typography variant="caption" display="block" color="text.secondary">{u.email}</Typography></TableCell>
                <TableCell>{u.nomeExterno || <Typography variant="body2" color="text.secondary">não ligado</Typography>}</TableCell>
                <TableCell>
                  <TextField id={`integ-u-${u.id}`} size="small" type="number" inputProps={{ min: 1 }}
                    value={edicao[u.id] !== undefined ? edicao[u.id] : (u.idUsuarioExterno || "")}
                    onChange={(e) => setEdicao({ ...edicao, [u.id]: e.target.value })} />
                </TableCell>
                <TableCell><Button size="small" disabled={edicao[u.id] === undefined} onClick={() => salvarUsuario(u)}>Salvar</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
